/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { getMarkerColor } from "../../Nodes/MapDisplay/helpers";
import { Spinner } from "../../../components/ui/spinner";
import { Card } from "../../../components/ui/card";

const GOOGLE_MAPS_API_KEY = (window as any).GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyAOxaq2JZHNi0euMmO2a2zk_-uHdKFu9HE";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 300px)",
  minHeight: "500px",
};

const DeviceLocationMap: React.FC = () => {
  const selectedDevice = useSelector(selectedDeviceSelector);
  const navigate = useNavigate();
  const [map, setMap] = React.useState<google.maps.Map | null>(null);
  const [isZooming, setIsZooming] = React.useState(false);
  const [fadeOut, setFadeOut] = React.useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Debug: Log the device coordinates
  React.useEffect(() => {
    if (selectedDevice) {
      console.log("DeviceLocationMap - Device coordinates:", {
        name: selectedDevice.name,
        latitude: selectedDevice.latitude,
        longitude: selectedDevice.longitude,
        latType: typeof selectedDevice.latitude,
        lngType: typeof selectedDevice.longitude,
      });
    }
  }, [selectedDevice]);

  // Convert coordinates from API format (microdegrees) to decimal degrees
  const microdegreesToDecimal = (microdegrees: number): number => {
    // Backend stores coordinates as microdegrees (degrees × 1,000,000)
    // For example: 26170710 = 26.170710°, -97667786 = -97.667786°
    return microdegrees / 1000000;
  };

  // Check if device has valid coordinates
  const hasValidLocation =
    selectedDevice?.latitude !== undefined &&
    selectedDevice?.latitude !== null &&
    selectedDevice?.latitude !== 0 &&
    selectedDevice?.longitude !== undefined &&
    selectedDevice?.longitude !== null &&
    selectedDevice?.longitude !== 0;

  const deviceLocation = hasValidLocation
    ? {
        lat: microdegreesToDecimal(selectedDevice.latitude),
        lng: microdegreesToDecimal(selectedDevice.longitude),
      }
    : null;

  const markerColor = getMarkerColor(selectedDevice);

  const handleMarkerClick = () => {
    if (!selectedDevice?.objectKey || !map || isZooming) return;

    setIsZooming(true);
    const maxZoom = 22; // Maximum zoom level for Google Maps
    
    // Zoom to max in one step
    map.setZoom(maxZoom);
    
  };

  if (loadError) {
    return (
      <div className="flex justify-center items-center h-[500px]">
        <h6 className="text-lg font-semibold text-red-500">
          Error loading Google Maps. Please check your API key configuration.
        </h6>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center h-[500px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hasValidLocation) {
    return (
      <div className="flex justify-center items-center h-[500px]">
        <h6 className="text-lg font-semibold text-muted-foreground">
          No location data available for this device
        </h6>
      </div>
    );
  }

  return (
    <div className="grid gap-2 p-2">
      <div className="w-full">
        <Card className="rounded-lg overflow-hidden">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={deviceLocation!}
            zoom={18}
            onLoad={(mapInstance) => setMap(mapInstance)}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              cameraControl: false,
              fullscreenControl: true,
              mapTypeId: 'satellite',
              zoomControl: !isZooming,
              gestureHandling: isZooming ? 'none' : 'auto',
            }}
          >
            <Marker
              position={deviceLocation!}
              title={selectedDevice?.name || "Device"}
              onClick={handleMarkerClick}
              cursor={isZooming ? 'wait' : 'pointer'}
              icon={{
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                  <svg width="110" height="110" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <style>
                        @keyframes pulse1 {
                          0% { opacity: 0.9; r: 12; }
                          100% { opacity: 0; r: 48; }
                        }
                        @keyframes pulse2 {
                          0% { opacity: 0.9; r: 12; }
                          100% { opacity: 0; r: 48; }
                        }
                        @keyframes pulse3 {
                          0% { opacity: 0.9; r: 12; }
                          100% { opacity: 0; r: 48; }
                        }
                        @keyframes coreGlow {
                          0%, 100% { filter: drop-shadow(0 0 4px ${markerColor}); }
                          50% { filter: drop-shadow(0 0 12px ${markerColor}); }
                        }
                        .pulse-ring1 { animation: pulse1 2s ease-out infinite; }
                        .pulse-ring2 { animation: pulse2 2s ease-out infinite 0.6s; }
                        .pulse-ring3 { animation: pulse3 2s ease-out infinite 1.2s; }
                        .core-circle { animation: coreGlow 2s ease-in-out infinite; }
                      </style>
                      <radialGradient id="glowGradient">
                        <stop offset="0%" stop-color="${markerColor}" stop-opacity="1"/>
                        <stop offset="70%" stop-color="${markerColor}" stop-opacity="0.6"/>
                        <stop offset="100%" stop-color="${markerColor}" stop-opacity="0"/>
                      </radialGradient>
                    </defs>
                    <circle class="pulse-ring1" cx="55" cy="55" r="12" fill="url(#glowGradient)" opacity="0.9"/>
                    <circle class="pulse-ring2" cx="55" cy="55" r="12" fill="url(#glowGradient)" opacity="0.9"/>
                    <circle class="pulse-ring3" cx="55" cy="55" r="12" fill="url(#glowGradient)" opacity="0.9"/>
                    <circle class="core-circle" cx="55" cy="55" r="12" fill="${markerColor}" stroke="white" stroke-width="3"/>
                  </svg>
                `)}`,
                scaledSize: new window.google.maps.Size(110, 110),
                anchor: new window.google.maps.Point(55, 55),
              }}
            />
          </GoogleMap>
        </Card>
      </div>
      <div className="w-full">
        <Card className="p-2 m-0 rounded-lg ">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-sm text-muted-foreground ml-1 mb-0">
                Latitude
              </p>
              <p className="text-base font-bold ml-1 mt-1">
                {deviceLocation?.lat.toFixed(6)}°
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground ml-1 mb-0">
                Longitude
              </p>
              <p className="text-base font-bold ml-1 mt-1">
                {deviceLocation?.lng.toFixed(6)}°
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DeviceLocationMap;
